import { useCallback, useEffect, useState } from 'react';
import { clientScopedFetch } from '../lib/authedFetch';
import { useViewingAs } from '../hooks/useViewingAs';
import GoogleBusinessOptions from './GoogleBusinessOptions';
import { editableGbpSettings, gbpSettingsProblem, gbpSettingsForSave, stripHashtagsForGoogle } from '../lib/gbp';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

// Google's post states, in words. DELETED is ours (see the DELETE route).
const STATE_LABELS = {
  LIVE:       { text: 'Live',               color: '#31DCBA' },
  RECURRING:  { text: 'Live · repeating',   color: '#31DCBA' },
  SCHEDULED:  { text: 'Scheduled on Google', color: 'var(--text-muted)' },
  PROCESSING: { text: 'Google is reviewing', color: '#f5a623' },
  REJECTED:   { text: 'Rejected by Google', color: '#FF5252' },
  DELETED:    { text: 'Deleted',            color: 'var(--text-muted)' },
};

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.ok === false) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.code = body.code;
    throw err;
  }
  return body;
}

// Posts that are live on Google, with Edit and Delete for the Google copy.
//
// Google is the one destination where a published post can still be changed,
// so this acts on Google only: Instagram, Facebook and TikTok are untouched.
// The post type is locked on edit — the API docs do not say a live post's type
// can change, so the server refuses it and this does not offer it.
export default function GoogleLivePosts({ clientId }) {
  const { viewingAsClientId } = useViewingAs();
  const [posts, setPosts]       = useState(null);   // null = loading
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing]   = useState(null);   // { post, settings }
  const [busyId, setBusyId]     = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [rowError, setRowError] = useState({});     // postId → message
  const [editError, setEditError] = useState('');

  const load = useCallback(() => {
    setLoadError('');
    return clientScopedFetch(`${API_BASE}/api/google-business/posts`)
      .then(readJson)
      .then(d => setPosts(d.posts || []))
      .catch(err => { setPosts([]); setLoadError(err.message); });
  }, []);

  useEffect(() => {
    if (!clientId) return;
    setPosts(null);
    load();
  }, [clientId, viewingAsClientId, load]);

  const setRowErr = (id, msg) => setRowError(prev => ({ ...prev, [id]: msg }));
  const patchLocal = (id, patch) => setPosts(prev => (prev || []).map(p => (p.id === id ? { ...p, ...patch } : p)));

  // Ask Google for the current state (catches a post Google later rejected,
  // or one deleted in Google's own app).
  const refreshState = async (post) => {
    setBusyId(post.id);
    setRowErr(post.id, '');
    try {
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/posts/${encodeURIComponent(post.id)}/google-business`));
      if (d.state === 'DELETED') setPosts(prev => prev.filter(p => p.id !== post.id));
      else patchLocal(post.id, { gbp_post_state: d.state, gbp_search_url: d.searchUrl });
    } catch (err) {
      setRowErr(post.id, err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (post) => {
    setBusyId(post.id);
    setRowErr(post.id, '');
    try {
      await readJson(await clientScopedFetch(`${API_BASE}/api/posts/${encodeURIComponent(post.id)}/google-business`, { method: 'DELETE' }));
      setPosts(prev => prev.filter(p => p.id !== post.id));
    } catch (err) {
      setRowErr(post.id, err.message);
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  };

  const openEdit = (post) => {
    const settings = editableGbpSettings(post.gbp_settings);
    // Pre-fill the text with what Google shows now, so editing starts from it.
    if (!settings.summary) settings.summary = googleTextFor(post);
    setEditError('');
    setEditing({ post, settings });
  };

  const saveEdit = async () => {
    const { post, settings } = editing;
    const problem = gbpSettingsProblem(settings);
    if (problem) { setEditError(problem); return; }
    setBusyId(post.id);
    setEditError('');
    try {
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/posts/${encodeURIComponent(post.id)}/google-business`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gbp_settings: gbpSettingsForSave(settings) }),
      }));
      patchLocal(post.id, { gbp_settings: d.gbp_settings, gbp_post_state: d.state });
      setEditing(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (posts === null) return null;
  if (!posts.length && !loadError) return null;

  return (
    <div className="cal-day-panel" style={{ marginTop:16 }} data-testid="gbp-live-posts">
      <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>Live on Google</div>
      <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:10, lineHeight:1.5 }}>
        Google is the one place a post can still be changed after it’s out. Edits and deletes here only touch Google.
      </div>
      {loadError && <div className="cq-error">{loadError}</div>}
      {posts.map(post => {
        const st = STATE_LABELS[post.gbp_post_state] || { text: post.gbp_post_state || 'Sent', color: 'var(--text-muted)' };
        const thumb = post.image_url || (Array.isArray(post.media_items) && post.media_items[0]) || null;
        const busy = busyId === post.id;
        return (
          <div key={post.id} className="cal-post-row" style={{ flexWrap:'wrap' }}>
            {thumb
              ? <img src={thumb} className="cal-post-thumb" style={{ objectFit:'cover', borderRadius:6 }} alt="" />
              : <div className="cal-post-thumb" style={{ background:'var(--accent-dim)' }} />}
            <div className="cal-post-caption" data-testid="gbp-live-text">{googleTextFor(post)}</div>
            <span className="cal-pill" style={{ color: st.color, borderColor: st.color }}>{st.text}</span>
            {confirmDeleteId === post.id ? (
              <span className="cal-post-confirm">
                <span className="cal-post-confirm-text">Delete from Google?</span>
                <button className="cal-post-btn cal-post-btn-danger" disabled={busy} onClick={() => remove(post)}>{busy ? '…' : 'Yes'}</button>
                <button className="cal-post-btn cal-post-btn-ghost" disabled={busy} onClick={() => setConfirmDeleteId(null)}>No</button>
              </span>
            ) : (
              <span className="cal-post-actions">
                {post.gbp_search_url && (
                  <a className="cal-post-btn cal-post-btn-ghost" href={post.gbp_search_url} target="_blank" rel="noopener noreferrer" title="Open on Google">↗ View</a>
                )}
                <button className="cal-post-btn cal-post-btn-ghost" disabled={busy} onClick={() => refreshState(post)} title="Check its status on Google">{busy ? '…' : '⟳ Status'}</button>
                <button className="cal-post-btn cal-post-btn-ghost" disabled={busy} onClick={() => openEdit(post)} title="Edit the Google post">✎ Edit</button>
                <button className="cal-post-btn cal-post-btn-delete" disabled={busy} onClick={() => setConfirmDeleteId(post.id)} title="Delete from Google" aria-label="Delete from Google">✕</button>
              </span>
            )}
            {rowError[post.id] && <div className="cq-error" style={{ width:'100%' }}>{rowError[post.id]}</div>}
          </div>
        );
      })}

      {editing && (
        <div className="chip-modal-overlay" onClick={() => busyId || setEditing(null)}>
          <div className="chip-modal" onClick={e => e.stopPropagation()} style={{ maxWidth:460 }}>
            <button className="chip-modal-close" onClick={() => busyId || setEditing(null)} aria-label="Close" title="Close">✕</button>
            <div className="chip-modal-body">
              <div style={{ fontSize:14, fontWeight:700, color:'var(--text-primary)' }}>Edit Google post</div>
              <GoogleBusinessOptions
                value={editing.settings}
                onChange={next => setEditing(prev => ({ ...prev, settings: next }))}
                lockType
                showSummary
              />
              {editError && <div className="cq-error">{editError}</div>}
              <button className="chip-modal-save" onClick={saveEdit} disabled={!!busyId}>
                {busyId ? 'Saving…' : 'Save to Google'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// What Google is showing for this post, which is what the row must show.
// An edit saves Google-only text in gbp_settings.summary; without one, Google
// has the caption with hashtags stripped (gbp-post.js gbpSummaryFor). Showing
// post.caption instead made a successful edit look like it hadn't saved.
export function googleTextFor(post) {
  const override = post && post.gbp_settings && post.gbp_settings.summary;
  return override ? override : stripHashtagsForGoogle((post && post.caption) || '');
}

